import { useState } from 'react';
import { VitalsSvgGraph } from './vitals-svg-graph';

export interface VitalReading {
  id: string;
  date: string | null;
  value: number;
  displayValue: string;
}

export interface MetricSectionProps {
  metricKey: string;
  label: string;
  unit: string;
  readings: VitalReading[];
  icon: JSX.Element;
  yAxisLabel?: string;
}

function formatReadingDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(dateStr));
}

function formatRelativeDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(date);
}

function TrendIndicator({ values, index }: { values: number[]; index: number }) {
  if (index === 0) return <span className="text-xs text-ink-subtle">—</span>;
  const prev = values[index - 1];
  const curr = values[index];
  if (curr > prev) return <span className="text-xs text-green-500">↑</span>;
  if (curr < prev) return <span className="text-xs text-red-400">↓</span>;
  return <span className="text-xs text-ink-subtle">→</span>;
}

const VITALS_COLOR = '#E05B8F';
const DEFAULT_SHOW = 3;

export function MetricSection({
  label,
  unit,
  readings,
  icon,
  yAxisLabel,
}: MetricSectionProps) {
  const [expanded, setExpanded] = useState(false);

  const latest = readings[readings.length - 1];
  const visibleReadings = expanded ? readings : readings.slice(-DEFAULT_SHOW);
  const hiddenCount = readings.length - DEFAULT_SHOW;
  const graphReadings = readings.map((r) => ({ date: r.date, value: r.value }));
  const allValues = readings.map((r) => r.value);

  return (
    <div className="bg-surface rounded-[20px] border border-border mx-4 mb-4 overflow-hidden">
      {/* Header */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-evt-vitals-ink">{icon}</span>
          <span className="text-xs font-bold tracking-widest uppercase text-evt-vitals-ink">
            {label}
          </span>
        </div>
        {readings.length === 0 ? null : (
          <>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-bold text-ink">{latest.displayValue}</span>
              <span className="text-sm text-ink-muted">{unit}</span>
            </div>
            <p className="text-xs text-ink-subtle mt-0.5">
              {formatRelativeDate(latest.date)}
            </p>
          </>
        )}
      </div>

      {readings.length === 0 ? (
        /* Empty state */
        <div className="flex flex-col items-center justify-center px-4 py-6 gap-2">
          <span className="text-evt-vitals-ink opacity-30">{icon}</span>
          <p className="text-sm text-ink-muted text-center">
            No {label.toLowerCase()} readings yet
          </p>
        </div>
      ) : (
        <>
          {/* Graph */}
          <div className="px-2 pt-1 pb-0">
            <VitalsSvgGraph
              readings={graphReadings}
              color={VITALS_COLOR}
              height={160}
              yAxisLabel={yAxisLabel}
            />
          </div>

          {/* Reading list */}
          <div className="border-t border-border mt-2">
            {[...visibleReadings].reverse().map((reading) => {
              const originalIndex = readings.indexOf(reading);
              return (
                <div
                  key={reading.id}
                  className="flex items-center justify-between px-4 py-2.5 border-b border-border last:border-b-0"
                >
                  <span className="text-sm text-ink-muted w-[90px] shrink-0">
                    {formatReadingDate(reading.date)}
                  </span>
                  <span className="text-sm font-semibold text-ink flex-1 text-center">
                    {reading.displayValue}{' '}
                    <span className="text-xs font-normal text-ink-muted">{unit}</span>
                  </span>
                  <div className="w-[24px] flex justify-end">
                    <TrendIndicator values={allValues} index={originalIndex} />
                  </div>
                </div>
              );
            })}

            {!expanded && hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="w-full min-h-[44px] flex items-center justify-center text-xs font-semibold text-accent hover:bg-canvas active:bg-canvas"
              >
                Show {hiddenCount} more {hiddenCount === 1 ? 'reading' : 'readings'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
